import { EngineTab } from '@/constants';
import { UnitMeasurementSystem } from '@/constants/enums';
import { omit } from 'lodash';
import { action, makeObservable, observable } from 'mobx';
import { StationParameter } from './common';
import { stationParametersEditableFields } from './constants';
import { convertFlowMinToMetric, convertPressureToImperial, convertPressureToMetric, isEmptyField } from './utils';
import { t } from 'i18next';

export class StationConfiguration {
  @observable stationParameter: StationParameter;
  @observable oldValues: Partial<StationConfiguration>;
  @observable isLoading = false;

  constructor(stationParametersConfiguration?, tvalue?, unitMeasurementSystem?) {
    makeObservable(this);
    this.isLoading = true;
    this.stationParameter = unitMeasurementSystem
      ? StationParameter.convertStationParameterToUnitMeasurementSystem(
          stationParametersConfiguration instanceof StationConfiguration
            ? [stationParametersConfiguration?.stationParameter]
            : [stationParametersConfiguration as StationParameter],
          tvalue,
          unitMeasurementSystem,
        )[0]
      : stationParametersConfiguration;

    if (!this.stationParameter || !unitMeasurementSystem) return;
    this.oldValues = this.oldValues ?? {};
    this.oldValues.stationParameter = this.stationParameter;
    this.isLoading = false;
  }

  @action setStationParameterField(key, field, value) {
    this.stationParameter = new StationParameter({ ...this.stationParameter, [field]: value });
  }

  hasChanges() {
    return stationParametersEditableFields.some(
      field => Number(this.stationParameter?.[field]) !== Number(this.oldValues?.stationParameter?.[field]),
    );
  }

  @action cancelChanges() {
    this.stationParameter = new StationParameter({
      ...this.oldValues?.stationParameter,
    });
  }

  @action copyStationParameters() {
    this.oldValues = { stationParameter: this.stationParameter };
  }

  getOldStationParameterByKey() {
    return this.oldValues?.stationParameter;
  }

  getValuesToSave(tvalue, unitMeasurementSystem) {
    const convertValue = (value, converter) =>
      value !== null && value !== undefined ? Number(converter(value, tvalue)) : null;
    const convertPressure = value =>
      convertValue(value, unitMeasurementSystem === UnitMeasurementSystem.Imperial ? convertPressureToMetric : v => v);
    const convertFlow = value =>
      convertValue(value, unitMeasurementSystem === UnitMeasurementSystem.Imperial ? convertFlowMinToMetric : v => v);

    const valuesToSave = new StationParameter({
      key: this.stationParameter.key,
      minAllowedUnloadTestingAttemptRateMinutes:
        Number(this.stationParameter.minAllowedUnloadTestingAttemptRateMinutes) || null,
      unloadCommandMinTargetSafetyPercentage: this.stationParameter.unloadCommandMinTargetSafetyPercentage / 100,
      minAllowedStartTimeIntervalMinutes: Number(this.stationParameter.minAllowedStartTimeIntervalMinutes) || null,
      minAllowedRestartTimeIntervalMinutes: Number(this.stationParameter.minAllowedRestartTimeIntervalMinutes) || null,
      unloadTestingFlowPeakBuffer: convertFlow(this.stationParameter.unloadTestingFlowPeakBuffer),
      minIntervalFromHighFlowTrigger: Number(this.stationParameter.minIntervalFromHighFlowTrigger) || null,
      unloadTestingCommandFrequencySeconds: Number(this.stationParameter.unloadTestingCommandFrequencySeconds) || null,
      kpiTimeIntervalMinutes: Number(this.stationParameter.kpiTimeIntervalMinutes) || null,
      emergencyStartPriorityRotationDelayMinutes:
        Number(this.stationParameter.emergencyStartPriorityRotationDelayMinutes) || null,
      compressorBovUpperBoundThresholdPercentage:
        this.stationParameter.compressorBovUpperBoundThresholdPercentage / 100,
      maxLoadBuffer: Number(this.stationParameter.maxLoadBuffer) / 100,
      unloadTestingFlowBufferPercentage: Number(this.stationParameter.unloadTestingFlowBufferPercentage) / 100,
      bovAvgUpperBoundThresholdPercentage: this.stationParameter.bovAvgUpperBoundThresholdPercentage / 100,
      minAllowedActiveCompressors: Number(this.stationParameter.minAllowedActiveCompressors) || null,
      unloadingTestPressureFactor: convertPressure(this.stationParameter.unloadingTestPressureFactor),
      normalPressureOffsetFromTarget: convertPressure(this.stationParameter.normalPressureOffsetFromTarget),
      loadBalancingPressureFactor: convertPressure(this.stationParameter.loadBalancingPressureFactor),
      stationTargetPressure: convertPressure(this.stationParameter.stationTargetPressure),
      reloadPressure: convertPressure(this.stationParameter.reloadPressure),
      startPressure: convertPressure(this.stationParameter.startPressure),
      emergencyStartPressure: convertPressure(this.stationParameter.emergencyStartPressure),
    });

    return omit(valuesToSave, 'oldValues');
  }

  validate(unitMeasurementSystem, tvalue?) {
    const validationMessages = [];
    const validateRequired = (parameter, field) => {
      if (isEmptyField(parameter[field])) {
        validationMessages.push({
          key: parameter?.key,
          field,
          message: t('required'),
          tArgs: { [field]: parameter?.[field] },
          tab: EngineTab.StationConfiguration,
        });
        return true;
      }
      return false;
    };

    const validateUnitMinMax = (parameter, field, title, min, max, unit = '') => {
      if (parameter[field] !== null && (parameter[field] < Number(min) || parameter[field] > Number(max))) {
        validationMessages.push({
          key: parameter?.key,
          field,
          message: t('validationForMinMax', { title, min, max, unit }),
          tArgs: { [field]: parameter?.[field] },
          tab: EngineTab.StationConfiguration,
        });
      }
    };

    const validateUnitGreaterThan = (parameter, field, title, min, unit = '') => {
      if (Number(parameter[field]) <= Number(min)) {
        validationMessages.push({
          key: parameter?.key,
          field,
          message: t('validationForGreaterThan', { title, min, unit }),
          tArgs: { [field]: parameter?.[field] },
          tab: EngineTab.StationConfiguration,
        });
      }
    };

    const defaultValidations = [
      {
        field: 'minAllowedUnloadTestingAttemptRateMinutes',
        title: 'Min frequency (min)',
        min: 15,
        max: 60,
        unit: 'min',
      },
      { field: 'unloadCommandMinTargetSafetyPercentage', title: 'Min pressure safety margin (%)', min: 2, max: 10 },
      {
        field: 'minAllowedStartTimeIntervalMinutes',
        title: 'Min compressor start interval (min)',
        min: 1,
        max: 5,
        unit: 'min',
      },
      {
        field: 'minAllowedRestartTimeIntervalMinutes',
        title: 'Min compressor restart interval (min)',
        min: 0,
        max: 30,
        unit: 'min',
      },
      { field: 'unloadTestingFlowPeakBuffer', title: 'Flow peak buffer', min: 0, max: 1000, unit: 'm3/min' },
      {
        field: 'minIntervalFromHighFlowTrigger',
        title: 'Min interval from high flow trigger',
        min: 5,
        max: 60,
        unit: 'min',
      },
      {
        field: 'unloadTestingCommandFrequencySeconds',
        title: 'Setpoint change interval (sec)',
        min: 5,
        max: 20,
        unit: 'sec',
      },
      { field: 'kpiTimeIntervalMinutes', title: 'KPI interval for retrieval (min)', min: 0, max: 30, unit: 'min' },
      {
        field: 'emergencyStartPriorityRotationDelayMinutes',
        title: 'Emergency start frequency (min)',
        min: 0,
        max: 5,
        unit: 'min',
      },
      { field: 'maxLoadBuffer', title: 'Full load pressure buffer (%)', min: 1, max: 10 },
      { field: 'unloadTestingFlowBufferPercentage', title: 'Unload testing flow buffer (%)', min: 0, max: 100 },
      { field: 'bovAvgUpperBoundThresholdPercentage', title: 'BOV min (%)', min: 0, max: 100 },
      { field: 'compressorBovUpperBoundThresholdPercentage', title: 'BOV max (%)', min: 70, max: 100 },
      {
        field: 'minAllowedActiveCompressors',
        title: 'Min no. of loaded compressors',
        min: 0,
        validate: validateUnitGreaterThan,
      },
    ];

    const convertValidationValues = validation => {
      if (unitMeasurementSystem === UnitMeasurementSystem.Imperial) {
        return {
          ...validation,
          min: tvalue ? convertPressureToImperial(validation.min, tvalue) : validation.min,
          max: tvalue ? convertPressureToImperial(validation.max, tvalue) : validation.max,
          unit: 'psi',
        };
      }
      return validation;
    };

    const unitValidations = [
      {
        field: 'normalPressureOffsetFromTarget',
        title: 'Offset from Target Pressure',
        min: 0.01,
        max: 0.4,
        unit: 'bar',
      },
      { field: 'unloadingTestPressureFactor', title: 'Pressure factor', min: 0.004, max: 0.02, unit: 'bar' },
      {
        field: 'loadBalancingPressureFactor',
        title: 'Load balancing pressure factor',
        min: 0.01,
        max: 0.1,
        unit: 'bar',
      },
    ].map(validation => convertValidationValues(validation));

    const requiredFields = [
      'stationTargetPressure',
      'normalPressureOffsetFromTarget',
      'reloadPressure',
      'startPressure',
      'emergencyStartPressure',
      'minAllowedActiveCompressors',
      'unloadingTestPressureFactor',
      'minAllowedUnloadTestingAttemptRateMinutes',
      'unloadCommandMinTargetSafetyPercentage',
      'minAllowedStartTimeIntervalMinutes',
      'minAllowedRestartTimeIntervalMinutes',
      'unloadTestingFlowPeakBuffer',
      'minIntervalFromHighFlowTrigger',
      'unloadTestingCommandFrequencySeconds',
      'kpiTimeIntervalMinutes',
      'loadBalancingPressureFactor',
      'emergencyStartPriorityRotationDelayMinutes',
      'compressorBovUpperBoundThresholdPercentage',
      'bovAvgUpperBoundThresholdPercentage',
      'maxLoadBuffer',
      'unloadTestingFlowBufferPercentage',
    ];
    const requiredValidation = requiredFields.map(field => ({ validate: validateRequired, field: field }));

    const validations = [...requiredValidation, ...defaultValidations, ...unitValidations];
    for (const validation of validations) {
      const { field, title, min, max, unit } = validation;
      const validateFn = 'validate' in validation ? validation.validate : validateUnitMinMax;
      validateFn(this.stationParameter, field, title, min, max, unit);
    }

    return validationMessages;
  }
}
